import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { XIcon, ExpandIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { gallery } from "../data/content";
import { SectionTitle } from "./SectionTitle";
import { Section } from "./Section";
import { surface } from "../data/theme";
const ease = [0.23, 1, 0.32, 1] as const;
export function Gallery() {
  const [active, setActive] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const reduceMotion = useReducedMotion();
  const total = gallery.pages.length;
  const open = openIndex !== null ? gallery.pages[openIndex] : null;
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    let closest = 0;
    let minDistance = Infinity;
    Array.from(track.children).forEach((child, i) => {
      const el = child as HTMLElement;
      const distance = Math.abs(el.offsetLeft + el.offsetWidth / 2 - center);
      if (distance < minDistance) {
        minDistance = distance;
        closest = i;
      }
    });
    setActive(closest);
  };
  const goTo = (index: number) => {
    const track = trackRef.current;
    const target = track?.children[index] as HTMLElement | undefined;
    if (!track || !target) return;
    track.scrollTo({
      left: target.offsetLeft - (track.clientWidth - target.offsetWidth) / 2,
      behavior: reduceMotion ? "auto" : "smooth"
    });
  };
  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenIndex(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIndex]);
  return <Section labelledBy="gallery-title" className="bg-mist text-center" waveTo={surface.violet} bleed data-id="element-69" data-mp="3tvhztgrx5i6k">
      <div className="px-5 sm:px-10" data-id="element-70" data-mp="3puppek7b978p">
        <p className="inline-flex rounded-full bg-gold/15 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.14em] text-gold-ink ring-1 ring-gold/35" data-id="element-71" data-mp="3ptmzoflq5aud">
          {gallery.eyebrow}
        </p>
        <SectionTitle id="gallery-title" title={gallery.title} highlight={gallery.highlight} className="mt-4" data-id="element-72" data-mp="2q036eyeauu7" />
        <p className="mt-4 leading-relaxed text-navy/80" data-id="element-73" data-mp="3b5jqkxcdxj1c">{gallery.text}</p>
      </div>

      <ul ref={trackRef} onScroll={handleScroll} aria-roledescription="carrusel" className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto px-[11%] pb-2 [scrollbar-width:none] sm:px-[20%] [&::-webkit-scrollbar]:hidden" data-id="element-74" data-mp="367s0m8vaw6zs">
        {gallery.pages.map((page, i) => <li key={page.src} aria-label={`${i + 1} de ${total}`} className="w-full shrink-0 snap-center" data-id="element-75" data-mp="2se92sp1yhmdo" data-mpi={i}>
            <button type="button" onClick={() => setOpenIndex(i)} className="group relative block w-full overflow-hidden rounded-2xl bg-white shadow-[0_18px_40px_-18px_rgba(13,42,71,0.5)] ring-1 ring-line focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold" aria-label={`Ampliar página ${i + 1}`} data-id="element-76" data-mp="2nghcu0kvgac4">
              <img src={page.src} alt={page.alt} loading="lazy" className="aspect-[3/4] w-full object-cover object-top" data-id="element-77" data-mp="ty9zxry4qv8i" />
              <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-navy/80 text-white" data-id="element-78" data-mp="xytv4pz57os0">
                <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-79" data-mp="1qmzilkhkzrvj" />
              </span>
            </button>
            {page.caption && <p className="mt-3 text-sm font-semibold text-navy" data-id="element-80" data-mp="1co9o8cm5c6av">{page.caption}</p>}
          </li>)}
      </ul>

      <div className="mt-5 flex items-center justify-center gap-4 px-5" data-id="element-81" data-mp="1hli76ff4ced6">
        <CarouselArrow label="Página anterior" disabled={active === 0} onClick={() => goTo(active - 1)} data-id="element-82" data-mp="17s0d0wm7bzu">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-83" data-mp="67xm60g7e4a0" />
        </CarouselArrow>
        <p className="text-sm text-muted" aria-live="polite" data-id="element-84" data-mp="lhkrsds4fz8o">
          <span className="font-bold text-navy" data-id="element-85" data-mp="lj4aeb192mdm">
            {active + 1} / {total}
          </span>{" "}
          — {gallery.hint}
        </p>
        <CarouselArrow label="Página siguiente" disabled={active === total - 1} onClick={() => goTo(active + 1)} data-id="element-86" data-mp="bqgdlga9siew">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-87" data-mp="7qo0qrdufq3q" />
        </CarouselArrow>
      </div>

      <AnimatePresence data-id="element-88" data-mp="14elzi6d28dgb">
        {open && <motion.div role="dialog" aria-modal="true" aria-label={open.alt} className="fixed inset-0 z-50 flex items-center justify-center bg-navy/90 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} onClick={() => setOpenIndex(null)} data-id="element-89" data-mp="14dsylm0s8i63">
            <button type="button" onClick={() => setOpenIndex(null)} className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20" aria-label="Cerrar" autoFocus data-id="element-90" data-mp="3g67rnjr1kwfm">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-91" data-mp="1vmvxfmoj6jw7" />
            </button>
            <motion.img src={open.src} alt={open.alt} className="max-h-[88vh] w-auto max-w-full rounded-lg" initial={reduceMotion ? false : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.22,
          ease
        }} onClick={e => e.stopPropagation()} data-id="element-92" data-mp="1zp0l84sasmax" />
          </motion.div>}
      </AnimatePresence>
    </Section>;
}
type CarouselArrowProps = {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
};
function CarouselArrow({
  label,
  disabled,
  onClick,
  children
}: CarouselArrowProps) {
  return <button type="button" aria-label={label} disabled={disabled} onClick={onClick} className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line bg-white text-navy transition-colors duration-150 ease-out hover:border-navy disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-line sm:flex" data-id="element-93" data-mp="t12dnxsu918e">
      {children}
    </button>;
}